import { ComponentType } from '@angular/cdk/portal';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Feature, Polygon } from 'geojson';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { MenuEventService } from 'src/app/layers/actions/map-actions/menu-event.service';
import { MapDrawService } from 'src/app/shared/components/map/map-draw.service';
import { MapService } from 'src/app/shared/components/map/map.service';
import { MenuPopupService } from 'src/app/shared/components/menus/menu-popup/menu-popup.service';
import { WindowService } from 'src/app/shared/components/window/window.service';
import { SimulationAreaFormComponent } from '../../simulation-area-form/simulation-area-form.component';
import { Simulation, SimulationParams } from '../../state/simulation.model';
import { SimulationQuery } from '../../state/simulation.query';
import { SimulationService } from '../../state/simulation.service';
import { ProjectDemographicAreasService } from '../../../layers/service/project-demographic-areas.service';
import { SimulationCollectiveClassificationComponent } from '../../simulation-collective-classification/simulation-collective-classification.component';

@Component({
  selector: 'app-simulation-coverage-area',
  templateUrl: './simulation-coverage-area.component.html',
  styleUrls: ['./simulation-coverage-area.component.scss']
})
export class SimulationCoverageAreaComponent implements OnInit, OnDestroy {

  isLoading: boolean;
  simulation$ = this.simulationQuery.active$;
  intersection: boolean;

  private simulationParams: SimulationParams;
  private subscriptions: Subscription[] = [];

  constructor(
    private drawService: MapDrawService,
    private windowService: WindowService,
    private simulationQuery: SimulationQuery,
    private simulationService: SimulationService,
    private menuEventService: MenuEventService,
    private mapService: MapService,
    private menuPopupService: MenuPopupService,
    private projectDemographicAreas: ProjectDemographicAreasService
  ) { }

  ngOnInit(): void {
    this.isLoading = true;

    this.simulationService.getSimulationParams()
      .pipe(
        take(1),
        finalize(() => this.isLoading = false)
      )
      .subscribe(params => this.simulationParams = params);

    this.menuEventService.active = false;
    this.drawService.enable();

    this.subscriptions.push(this.drawService.create$.subscribe(f => this.addArea(f)));
    this.subscriptions.push(this.drawService.update$.subscribe(f => this.updateArea(f)));
    this.subscriptions.push(this.drawService.delete$.subscribe(f => this.deleteArea(f)));

    this.checkIntersection(this.simulationQuery.getActive());
    this.listenDemographicAreaClick();
  }

  ngOnDestroy(): void {
    this.drawService.disable();

    this.subscriptions.forEach(s => s.unsubscribe());

    this.menuEventService.active = true;

    // @TODO verificar
    this.mapService.map$
      .pipe(take(1))
      .subscribe(map => {
        map.off('contextmenu', this.demographicAreaClickFn);
        this.projectDemographicAreas.getAllLayerIds().forEach(layer => {
          map.off('contextmenu', layer, this.demographicAreaClickFn);
        });
      });
  }

  classify(): void {
    this.open($localize`CLASSIFICATION OF COLLECTIVE PROPERTIES`, SimulationCollectiveClassificationComponent);
  }

  validate() {
    this.open($localize`NETWORK AREA ATTENDANCE RATES`, SimulationAreaFormComponent);
  }

  close() {
    this.simulationService.setCoverageMode(false);
  }

  private demographicAreaClickFn = (e) => {
    if (this.drawService.hasSelected() || e.defaultPrevented) {
      return;
    }

    e.preventDefault();

    let data: Feature<Polygon>;

    if (e.features?.length) {

      const feature: Feature<Polygon> = e.features[0];
      data = {
        id: 'mitra-' + Date.now(),
        type: 'Feature',
        geometry: feature.geometry,
        properties: {}
      };
    } else {
      data = null;
    }

    const top = e.originalEvent.clientY;
    const left = e.originalEvent.clientX;
    const component = CoverageMenuComponent;
    this.menuPopupService.open({ component, left, top, data });
  }

  private addArea(feature: Feature<Polygon>) {
    const simulationId = this.simulationQuery.getActiveId();
    this.simulationService.addArea(simulationId, feature, this.simulationParams)
      .pipe(take(1))
      .subscribe(s => this.checkIntersection(s));
  }

  private updateArea(feature: Feature<Polygon>) {
    const simulation = this.simulationQuery.getActive();

    this.simulationService.updateArea(simulation.id, feature, this.simulationParams)
      .pipe(take(1))
      .subscribe(s => this.checkIntersection(s));
  }

  private checkIntersection(simulation: Simulation) {

    if (simulation.areas_definition.length) {
      this.intersection = this.simulationService.checkIntersectionAreas(simulation);
    }
  }

  private deleteArea(feature: Feature<Polygon>) {
    const simulation = this.simulationQuery.getActive();
    this.simulationService.removeArea(simulation.id, feature.id as string, this.simulationParams)
      .pipe(take(1))
      .subscribe(s => this.checkIntersection(s));
  }

  private open(title: string, componentType: ComponentType<any>) {
    this.windowService.open({ title, componentType, help: 'COVERAGE_AREA' });
  }

  private listenDemographicAreaClick() {
    // @TODO Verificar
    this.mapService.map$.subscribe(map => {
      // map.on('contextmenu', DEMOGRAPHIC_LAYER_ID, this.demographicAreaClickFn);
      this.projectDemographicAreas.getAllLayerIds().forEach(layer => {
        map.off('contextmenu', layer, this.demographicAreaClickFn);
      });
      map.on('contextmenu', this.demographicAreaClickFn);
    });
  }
}

@Component({
  template: `
    <button mat-menu-item mat-dialog-close (click)="newSimulationArea()" i18n>NEW NETWORK AREA</button>
    <button *ngIf="feature" mat-menu-item mat-dialog-close (click)="createArea()" i18n>COVER ECONOMIC CLASS AREA</button>
  `
})
export class CoverageMenuComponent implements OnInit {

  constructor(
    @Inject(MAT_DIALOG_DATA) public feature: Feature<Polygon>,
    private mapDrawService: MapDrawService
  ) {
  }

  ngOnInit(): void { }

  createArea() {
    this.mapDrawService.addAreaAndNotify(this.feature);
  }

  newSimulationArea() {
    this.mapDrawService.enableArea();
  }

}
